import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";

import { getRol, getToken, getUser } from "../../../api/Account";
import { getDesembolsosAsync } from "../../../api/Report";
import { getGestoresAsync } from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import { formatearFecha, formatearMonto } from "../../../helpers/formato";
import ReporteAgrupado from "../../../components/reportes/ReporteAgrupado";

const TODOS = "t";

const dinero = (key, label, total = false) => ({
  key,
  label,
  align: "center",
  total,
  formatoTotal: formatearMonto,
  valor: (c) => formatearMonto(c[key]),
  excel: (c) => c[key],
});

// Las fechas ISO se comparan bien como texto: así el orden inicial es el cronológico.
const COLUMNAS = [
  {
    key: "fechaEntrega",
    label: "F.Entrega",
    align: "center",
    texto: true,
    valor: (c) => formatearFecha(c.fechaEntrega),
    excel: (c) => formatearFecha(c.fechaEntrega),
  },
  {
    key: "fullName",
    label: "Cliente",
    align: "left",
    texto: true,
    valor: (c) => c.fullName,
  },
  {
    key: "tipoCliente",
    label: "Tipo",
    align: "center",
    texto: true,
    valor: (c) => c.tipoCliente,
  },
  dinero("montoPrincipal", "Principal", true),
  {
    // Es una tasa en %, no un monto: no se suma.
    key: "interesCorriente",
    label: "I.Corriente",
    align: "center",
    valor: (c) => `${c.interesCorriente.toFixed(2)}%`,
    excel: (c) => c.interesCorriente,
  },
  dinero("montoPagar", "M.Pagar", true),
  {
    key: "fechaPrimeraCuota",
    label: "F.Cuota1",
    align: "center",
    valor: (c) => formatearFecha(c.fechaPrimeraCuota),
    excel: (c) => formatearFecha(c.fechaPrimeraCuota),
  },
  dinero("cuotaInteresCorriente", "C.Int.Corriente"),
  dinero("cuotaPrincipal", "C.Capital"),
  dinero("cuotaPagar", "C.Pagar"),
  {
    key: "periodicidad",
    label: "Periodicidad",
    align: "center",
    texto: true,
    valor: (c) => c.periodicidad,
  },
  {
    key: "plazo",
    label: "Plazo",
    align: "center",
    texto: true,
    valor: (c) => c.plazo,
  },
  {
    key: "isCanceled",
    label: "Cancelado",
    align: "center",
    valor: (c) => (c.isCanceled ? "SI" : "NO"),
    excel: (c) => c.isCanceled,
  },
];

// Mismas fórmulas de siempre. "Total Desembolsos" ya sale en el título de cada gestor y en
// el TOTAL GENERAL; Principal y Monto a Pagar, en la fila de totales. Aquí van las cifras
// que no son la suma de una columna: nuevos, recurrentes y el interés corriente total
// (monto a pagar menos principal).
const resumen = (filas) => [
  {
    label: "Total Nuevos",
    valor: filas.filter((c) => c.tipoCliente === "N").length,
  },
  {
    label: "Total Recurrentes",
    valor: filas.filter((c) => c.tipoCliente === "R").length,
  },
  {
    label: "Total Interes Corriente",
    valor: formatearMonto(
      filas.reduce((total, c) => total + (c.montoPagar - c.montoPrincipal), 0)
    ),
  },
];

const Desembolsos = () => {
  const [data, setData] = useState([]);
  const [gestorName, setGestorName] = useState("Todos");

  const { selectedGestor, fechaDesde, fechaHasta } = useParams();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const manejarError = useCallback(
    (result) => {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
    },
    [setIsUnautorized]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [reporte, gestores] = await Promise.all([
        getDesembolsosAsync(token, {
          desde: fechaDesde.toString(),
          hasta: fechaHasta.toString(),
          gestorId: selectedGestor === TODOS ? 0 : selectedGestor,
        }),
        selectedGestor === TODOS ? null : getGestoresAsync(token),
      ]);
      setIsLoading(false);

      if (!reporte.statusResponse) {
        manejarError(reporte);
        return;
      }
      setData(reporte.data.result);

      if (selectedGestor !== TODOS) {
        // Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
        const gestor =
          gestores && gestores.statusResponse
            ? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
            : null;
        setGestorName(
          gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
        );
      }
    })();
  }, [token, selectedGestor, fechaDesde, fechaHasta, setIsLoading, manejarError]);

  // Gestor del crédito (Credits.Gestor), con el nombre que manda el servidor: también el
  // de un gestor dado de baja.
  const agruparPor = useCallback((fila) => fila.gestorNombre, []);

  return (
    <ReporteAgrupado
      tituloReporte="Reporte de Desembolsos"
      nombreArchivo="Desembolsos"
      parametros={[
        { label: "Gestor Seleccionado", valor: gestorName },
        { label: "Desde", valor: formatearFecha(fechaDesde) },
        { label: "Hasta", valor: formatearFecha(fechaHasta) },
      ]}
      columnas={COLUMNAS}
      data={data}
      agruparPor={agruparPor}
      resumen={resumen}
      ordenInicial="fechaEntrega"
      contexto={{ title, slogan, icon, user, rol }}
    />
  );
};

export default Desembolsos;
